import React, {memo, useEffect} from "react";
import {Button} from "@/components/ui/Button";
import {CircleCheck, CloudOff, Loader, RefreshCw} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {SavingState} from "@/types/node";
import {AnimatePresence, motion} from "framer-motion";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";

const AutoSave: React.FC<{disabled?: boolean}> = ({disabled = false}) => {
  const {handleSave, savingState} = useSave();

  const animationProps = {
    initial: {opacity: 0, y: 10},
    animate: {opacity: 1, y: 0},
    exit: {opacity: 0, y: -10},
    transition: {duration: 0.07, ease: "easeOut"},
  };

  return (
    <motion.div
      className="flex items-center justify-center text-sm font-medium w-25"
      {...animationProps}
    >
      <AnimatePresence mode="wait">
        {savingState === SavingState.SAVED && (
          <motion.div
            className={cn(
              "flex items-center gap-1 text-green-dark px-2 py-1.5 rounded-md",
              disabled && "text-disabled",
            )}
            key="saved"
            {...animationProps}
          >
            <CircleCheck size={16} />
            All Saved
          </motion.div>
        )}
        {savingState === SavingState.SAVING && (
          <motion.div
            className="flex items-center gap-1 text-label px-2 py-1.5 rounded-md"
            key="saving"
            {...animationProps}
          >
            <Loader size={16} className="animate-spin" />
            Saving...
          </motion.div>
        )}
        {savingState === SavingState.LOADING && (
          <motion.div
            className="flex items-center gap-1 text-label px-2 py-1.5 rounded-md"
            key="saving"
            {...animationProps}
          >
            <Loader size={16} className="animate-spin" />
            Loading...
          </motion.div>
        )}
        {savingState === SavingState.ERROR && (
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.div
                className="flex items-center gap-1 text-orange-dark hover:border hover:border-orange-600/20 bg-orange-600/15 cursor-pointer px-2 py-1.5 rounded-md"
                key="error"
                {...animationProps}
                onClick={() => handleSave()}
              >
                <CloudOff size={16} />
                Unsaved
              </motion.div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              className="bg-slate-800 rounded-md p-3 w-[210px] border-0"
            >
              <TooltipArrow className="fill-slate-800" />
              <div className="flex items-center flex-col gap-3 text-sm font-medium">
                <p className="text-white">
                  You have unsaved changes. Do not close the page to avoid
                  losing all progress.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-[#161618] font-medium"
                  onClick={() => handleSave()}
                >
                  <RefreshCw size={16} />
                  Try Again
                </Button>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default memo(AutoSave);
